import { BaseManager } from '../core/baseManager';
import { windowScope } from '../window/windowScope';

interface UtilityState {
    verticalScroll: number;
    inMobile: boolean;
}

export class UtilityManager extends BaseManager {
    private state: UtilityState;
    private body: HTMLElement;

    constructor() {
        super('UtilityManager');
        this.body = document.querySelector('body') as HTMLElement;
        this.state = {
            verticalScroll: window.scrollY,
            inMobile: window.matchMedia('(max-width: 991px)').matches
        };
    }

    public initialize(): void {
        super.initialize();
        this.copyRightHandler();
        this.verticalScrollHandler();
        this.magicAlignContainerHandler();
        this.lazyLoadBackgrounds();
        this.detailBreadcrumbModifierHandler();
        this.resetBodyHandler();
    }

    public copyRightHandler(): void {
        const copyYear = document.querySelector('#copyYear') as HTMLElement;
        if (copyYear !== null) {
            const currentYear = new Date().getFullYear();
            copyYear.textContent = currentYear.toString();
        }
    }

    public verticalScrollHandler(): void {
        let scrollSlack = 0;
        if (!this.state.inMobile) {
            scrollSlack = 32;
        }

        this.state.verticalScroll = window.scrollY;

        if (this.state.verticalScroll > scrollSlack) {
            if (!this.body.classList.contains('scrolling')) {
                this.body.classList.add('scrolling');
            }
        } else {
            if (this.body.classList.contains('scrolling')) {
                this.body.classList.remove('scrolling');
            }
        }
    }

    public magicAlignContainerHandler(): void {
        // Keeps magic containers left aligned with normal containers so that they can overflow on the right
        const magicContainers = document.querySelectorAll('.magic-aligns-to-containers') as NodeListOf<HTMLElement>;

        if (magicContainers.length) {
            const magicContainerControlEl = document.querySelector('.magic-container-control-element') as HTMLElement;
            if (magicContainerControlEl) {
                const magicContainerControlElDist = magicContainerControlEl.getBoundingClientRect().left + 15;

                magicContainers.forEach((element: HTMLElement) => {
                    if (element.classList.contains('magic-aligns-to-containers--right')) {
                        element.style.paddingRight = magicContainerControlElDist + "px";
                    } else {
                        element.style.paddingLeft = magicContainerControlElDist + "px";
                    }
                });
            }
        }
    }

    private lazyLoadBackgrounds(): void {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target as HTMLElement;
                    const bgSrc = element.dataset.bgSrc;
                    if (bgSrc) {
                        element.style.backgroundImage = `url(${bgSrc})`;
                        element.removeAttribute('data-bg-src');
                    }
                    observer.unobserve(element);
                }
            });
        });

        document.querySelectorAll('[data-bg-src]').forEach(el => {
            observer.observe(el);
        });
    }

    private detailBreadcrumbModifierHandler(): void {
        windowScope.detailBreadcrumbModifier = (title: string) => {
            const currentBread = document.querySelector('.breadcrumb-item.active') as HTMLElement;
            if (currentBread) {
                currentBread.innerHTML = title;
            }
        };
    }

    private resetBodyHandler(): void {
        windowScope.resetBody = () => {
            this.body.classList.remove('dim', 'dim--invisi', 'search-active', 'disable-main-nav');
            windowScope.closeMobNav(true);
            const currentlyOpenNavs = document.querySelectorAll('.nav-item .show');
            if (currentlyOpenNavs.length) {
                currentlyOpenNavs.forEach(function (e) {
                    e.classList.remove('show');
                });
            }
            const currentlyOpenFilters = document.querySelectorAll('.sbx-facets.active');
            if (currentlyOpenFilters.length) {
                currentlyOpenFilters.forEach(function (e) {
                    e.classList.remove('active');
                });
            }
        };
    }

    private lastDimensions = { width: window.innerWidth, height: window.innerHeight };

    public onResize(): void {
        const current = { width: window.innerWidth, height: window.innerHeight };
        if (current.width === this.lastDimensions.width && current.height === this.lastDimensions.height) return; // only do stuff if dimentions actually changed
        this.lastDimensions = current;

        this.state.inMobile = window.matchMedia('(max-width: 991px)').matches;
        this.verticalScrollHandler();
        this.magicAlignContainerHandler();
        windowScope.resetBody();
    }

    public onScroll(): void {
        this.verticalScrollHandler();
    }

    // Getter for accessing mobile state
    public get isMobile(): boolean {
        return this.state.inMobile;
    }

    // Getter for accessing vertical scroll position
    public get scrollPosition(): number {
        return this.state.verticalScroll;
    }
}
